import type { PaymentIntent } from "@stripe/stripe-js";

import { triggerCustomerIoOnboardingPaymentCompleted } from "@/features/analytics/triggers/customer-io/onboarding";
import {
  categoriesToGoogleTagManagerProducts,
  handleAnalyticsError,
} from "@/features/analytics/utils";
import type { OnboardingUser } from "@/features/onboarding/models";
import type { UrlParamsSlice } from "@/features/onboarding/stores";
import type {
  CategoryWithSelectedProduct,
  CategoryWithSelectedProductRequired,
} from "@/features/products/models";
import { calculateTotal } from "@/features/products/utils";
import type { Invoice } from "@/shared/domains";
import { CURRENCY } from "@/shared/models";
import type { EventPrefix, GoogleTagManagerPurchaseDetails } from "../models";
import { EVENT_PREFIX, GOOGLE_TAG_MANAGER_EVENT } from "../models";
import { triggerGoogleTagManagerTracking } from "../triggers";

interface TrackPaymentCompletedParams {
  categories: CategoryWithSelectedProduct[];
  succeededPaymentIds: PaymentIntent["id"][];
  succeededInvoiceIds: Invoice["id"][];
  user: OnboardingUser;
  metadata: UrlParamsSlice["urlParams"];
  eventPrefix?: EventPrefix;
}

export const trackPaymentCompleted = ({
  categories,
  succeededPaymentIds,
  succeededInvoiceIds,
  user,
  metadata,
  eventPrefix = EVENT_PREFIX.NONE,
}: TrackPaymentCompletedParams) => {
  try {
    const paymentIntentBasedOrderId = succeededPaymentIds.join("-");
    const totalPrice = calculateTotal(categories);
    const invoiceBasedOrderId = succeededInvoiceIds.join("-");
    const categoriesWithSelectedPrice = categories.filter(
      (category) => category.selectedProduct?.selectedPrice,
    ) as CategoryWithSelectedProductRequired[];

    triggerGoogleTagManagerTracking<GoogleTagManagerPurchaseDetails>({
      event: `${eventPrefix}${GOOGLE_TAG_MANAGER_EVENT.PAYMENT_COMPLETED}`,
      ecommerce: {
        value: totalPrice,
        currency: CURRENCY.USD,
        transaction_id: paymentIntentBasedOrderId,
        invoice_transaction_id: invoiceBasedOrderId,
        items: categoriesToGoogleTagManagerProducts(
          categoriesWithSelectedPrice,
        ),
      },
    });

    triggerCustomerIoOnboardingPaymentCompleted({
      categories,
      succeededPaymentIds,
      user,
      metadata,
      eventPrefix,
    });
  } catch (error) {
    handleAnalyticsError(error, {
      message: "Error tracking payment completed event",
    });
  }
};
